import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CommunityLayout from "../components/layouts/CommunityLayout";
import { usePageTitle } from '../hooks/usePageTitle';
import { CommunicationBoardOptions } from '../utils';
import { getSessionData } from '../utils/auth';

const toolTags = ['Drawing Pad', 'Keyboard', 'Assistive Keyboard', 'AAC Board'];

const SeeMorePopover = ({ content }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const timeoutRef = useRef(null);

    const open = useCallback(() => {
        clearTimeout(timeoutRef.current);
        setIsOpen(true);
    }, []);

    const close = useCallback(() => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
    }, []);

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <span ref={containerRef} className="relative inline-block">
            <span
                role="button"
                tabIndex={0}
                className="text-xs text-purple-300 underline underline-offset-2 decoration-dotted cursor-pointer hover:text-white transition-colors"
                onMouseEnter={open}
                onMouseLeave={close}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen((v) => !v);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOpen((v) => !v);
                    }
                }}
            >
                See more
            </span>

            {isOpen && (
                <div
                    className="absolute z-50 w-72 sm:w-80 p-4 bottom-full mb-2 left-1/2 -translate-x-1/2 rounded-xl bg-[#1e0338]/95 border border-purple-400/30 shadow-2xl backdrop-blur-sm"
                    onMouseEnter={open}
                    onMouseLeave={close}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-purple-400/30" />

                    <h4 className="text-sm font-bold text-white mb-2">
                        {content.title}
                    </h4>
                    <ul className="space-y-1.5">
                        {content.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-white/80 text-left">
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </span>
    );
};

const CommunicationToolsLanding = () => {
    usePageTitle('Communication Tools - Wefayo');
    const navigate = useNavigate();

    useEffect(() => {
        if (getSessionData() === null) {
            navigate('/login');
        }
    }, [navigate]);

    const handleSelectTool = (option) => {
        navigate(`/cb/${option.module}`);
    };

    return (
        <CommunityLayout
            currentPage="cb"
            onCommunitiesLoaded={() => { }}
            isFullPageWidth={true}
            overflow="overflow-y-auto"
            px="px-0"
            mustShowFooter={true}
        >
            {/* ── Full-width gradient wrapper ──────────────────────────────────── */}
            <div
                className="relative w-full flex pt-0 md:pt-[5px] lg:pt-[57px] h-[calc(100vh-57px)] overflow-y-auto"
                style={{ background: 'linear-gradient(135deg, #2e0454 0%, #581c87 55%, #7c3aed 100%)' }}
            >
                {/* Subtle dot-grid overlay */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-10"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                        backgroundSize: '30px 30px',
                    }}
                />

                {/* ── Two-column layout ──────────────────────────────────────────── */}
                <div className="relative flex flex-col lg:flex-row items-start justify-between gap-10 px-6 md:px-12 lg:px-16 py-16 md:py-20 mx-auto max-w-8xl xl:max-w-[1500px]">

                    {/* ── LEFT — Intro / hero copy ───────────────────────────────── */}
                    <div className="h-full flex flex-col items-start justify-center  text-left lg:max-w-[40%] lg:sticky lg:top-8">



                        {/* Icon ring */}
                        <div className="hidden lg:flex w-16 h-16 rounded-full bg-white/10 border border-white/20  items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 0 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 1 8.835-2.535m0 0A23.74 23.74 0 0 1 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m-1.394 5.52A21.04 21.04 0 0 1 18 18.75m.795-14.625A21.04 21.04 0 0 1 18 5.25" />
                            </svg>
                        </div>


                        {/* Heading */}
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight flex flex-row items-start lg:items-center  ">
                            <div className="lg:hidden !w-16 !h-16 flex-shrink-0 rounded-full bg-white/10 border border-white/20 flex items-center justify-center ">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 0 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 1 8.835-2.535m0 0A23.74 23.74 0 0 1 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m-1.394 5.52A21.04 21.04 0 0 1 18 18.75m.795-14.625A21.04 21.04 0 0 1 18 5.25" />
                                </svg>
                            </div>
                            <div className="pl-2 lg:pl-0 ">
                                Express yourself,{' '}
                                <span className="italic text-purple-300">your way.</span>
                            </div>
                        </h1>

                        {/* Description */}
                        <p className="text-white/80 text-base mb-8">
                            A free suite of communication tools designed to help everyone &mdash; especially those with disabilities &mdash; express
                            themselves clearly and confidently in any situation.
                        </p>

                        {/* Badge */}
                        <div className="mt-5 lg:mt-20 self-start inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/90 text-xs font-semibold mb-6 tracking-widest uppercase">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-300 inline-block" />
                            FREE &middot; ACCESSIBLE &middot; EXPRESSIVE
                        </div>

                        {/* Tool type pills
                        <div className="flex flex-wrap gap-2 mb-10">
                            {toolTags.map((tag) => (
                                <span
                                    key={tag}
                                    className="bg-white/10 border border-white/25 text-white text-xs px-3 py-1 rounded-full"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div> */}

                        {/* <p className="text-white/40 text-xs mt-1">
                            All tools are free to use. No account required.
                        </p> */}
                    </div>

                    {/* ── RIGHT — Tool cards ─────────────────────────────────────── */}
                    <div className="flex flex-col w-full lg:max-w-[60%] items-center justify-center md:h-full">

                        <h2 className="text-xl font-bold text-white text-center mb-1">
                            Select a Communication Tool to begin
                        </h2>
                        <p className="text-white/60 text-sm text-center mb-6">
                            Pick the tool that best fits your communication needs right now.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {CommunicationBoardOptions.map((option) => (
                                <button
                                    key={option.id}
                                    onClick={() => handleSelectTool(option)}
                                    className="flex flex-col items-center p-4 md:p-5 rounded-xl border-2 border-white/20 bg-white/5 hover:bg-white/15 hover:border-purple-300 hover:shadow-lg transition-all duration-200 group text-center"
                                >
                                    <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 rounded-full flex items-center justify-center p-2 mb-3 group-hover:bg-purple-500/40 transition-colors duration-200">
                                        <img
                                            alt={option.name}
                                            src={option.icon}
                                            className="w-12 h-auto md:w-16"
                                            style={{
                                                maxWidth: '100%',
                                                height: option?.iconHeight ?? 'auto',
                                                minHeight: option?.minHeight ?? 'auto',
                                            }}
                                        />
                                    </div>
                                    <h3 className="font-bold text-white text-lg mb-1">
                                        {option.name}
                                    </h3>
                                    <p className="text-md text-purple-200 font-semibold mb-2">
                                        {option.description}
                                    </p>
                                    {option.seeMoreContent && (
                                        <SeeMorePopover content={option.seeMoreContent} />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </CommunityLayout>
    );
};

export default CommunicationToolsLanding;
